import { useCallback, useEffect, useRef, useState } from 'react';
import haxios from '../lib/haxios';
import { myUnread } from '../lib/chatApi';

// useUnreadMessages — a GLOBAL unread-chat signal for the signed-in user,
// independent of any customer list. This is the answer to "someone got pulled
// into a thread for a customer that isn't mine — how do I see it?": the
// dashboard customer column only covers the user's own customers, so it can't.
//
// Data source, in priority order:
//   1. GET /chat/my-unread — the dedicated endpoint (authoritative message-level
//      unread across every thread the caller participates in). Preferred.
//   2. Fallback: the notifications feed. A new private-thread comment always
//      fans out a `visit_comment` (or `chat`, for @mentions/replies)
//      notification to connected staff regardless of customer ownership, so the
//      unread ones approximate "unread messages" and work today with no backend
//      change. We group them by deep-link so one thread = one row.
//
// Returns { total, threadCount, threads, loading, source, refresh }. Each thread
// is normalised to { key, title, detail, unread, at, link, entityType, entityId }.
//
// Pass { enabled: false } to mount the hook without polling — used when a parent
// already owns the poll and hands the state down (e.g. the dashboard KPI tile
// and side panel share one poll instead of two).

const POLL_MS = 30000;
const COMMENT_CATEGORIES = new Set(['visit_comment', 'chat']);

const normRich = (t) => ({
  key: t.link || `${t.entityType}:${t.entityId}`,
  title: t.label || t.title || `${t.entityType || 'Thread'} ${t.entityId || ''}`.trim(),
  detail: t.lastSnippet
    ? `${t.lastAuthor ? `${t.lastAuthor}: ` : ''}${t.lastSnippet}`
    : (t.detail || ''),
  unread: Number(t.unread) || 0,
  at: t.lastAt || t.at || null,
  link: t.link || null,
  entityType: t.entityType,
  entityId: t.entityId,
});

// ISO-8601 timestamps sort correctly as plain strings, so newest-first is a
// descending string compare on `at`.
const byNewest = (a, b) => String(b.at || '').localeCompare(String(a.at || ''));

const fromNotifications = (payload) => {
  const list = Array.isArray(payload) ? payload : (payload?.notifications || []);
  const unread = list.filter(n =>
    !n.read && !n.readAt && COMMENT_CATEGORIES.has(String(n.category || n.type)));
  // Collapse multiple comment pings on the same thread into one row.
  const map = new Map();
  for (const n of unread) {
    const link = n.link || `note:${n.id}`;
    const at = n.createdAt || n.created_at || null;
    const existing = map.get(link);
    if (existing) {
      existing.unread += 1;
      if (at && (!existing.at || at > existing.at)) {
        existing.at = at;
        existing.title = n.title || n.message || existing.title;
        existing.detail = n.detail || existing.detail;
      }
    } else {
      map.set(link, {
        key: link,
        title: n.title || n.message || 'New message',
        detail: n.detail || '',
        unread: 1,
        at,
        link: n.link || null,
      });
    }
  }
  const threads = [...map.values()].sort(byNewest);
  return { total: unread.length, threadCount: threads.length, threads };
};

export const useUnreadMessages = ({ enabled = true } = {}) => {
  const [state, setState] = useState({
    total: 0, threadCount: 0, threads: [], loading: true, source: null,
  });
  // Once the dedicated endpoint 404s we stop hammering it and stay on the
  // notifications fallback for the rest of the session.
  const richDisabled = useRef(false);

  const load = useCallback(async () => {
    if (!richDisabled.current) {
      try {
        const r = await myUnread();
        if (r && (Array.isArray(r.threads) || typeof r.total === 'number')) {
          const threads = (r.threads || []).map(normRich).sort(byNewest);
          setState({
            total: typeof r.total === 'number'
              ? r.total
              : threads.reduce((a, t) => a + t.unread, 0),
            threadCount: r.threadCount != null ? r.threadCount : threads.length,
            threads,
            loading: false,
            source: 'chat',
          });
          return;
        }
      } catch (err) {
        if (err?.response?.status === 404) richDisabled.current = true;
        // Any other error → fall through and try the notifications feed.
      }
    }
    try {
      const notes = await haxios.get('/notifications');
      setState({ ...fromNotifications(notes), loading: false, source: 'notifications' });
    } catch {
      setState(s => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let dead = false;
    const tick = () => { if (!dead) load(); };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { dead = true; clearInterval(id); };
  }, [load, enabled]);

  return { ...state, refresh: load };
};

export default useUnreadMessages;
